import { z } from 'zod';
import { IdSchema, LanguageSchema } from './common.js';
import { PasswordPolicy } from './auth.js';

// ----- Ubicaciones -----
export const LocationSchema = z.object({
  id: z.number().int(),
  code: z.string(),
  name: z.string(),
  type: z.enum(['WAREHOUSE', 'STORE']),
  active: z.boolean(),
  can_distribute: z.boolean(),
  min_total: z.number().int(),
  /** Gastos fijos mensuales. Solo llegan a quien administra la configuración o ve el dashboard; para los demás, null. */
  rent: z.number().nullable().optional(),
  payroll: z.number().nullable().optional(),
  other_fixed: z.number().nullable().optional(),
});
export type Location = z.infer<typeof LocationSchema>;

export const CreateLocationBodySchema = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_-]{1,12}$/),
  name: z.string().trim().min(1).max(80),
  type: z.enum(['WAREHOUSE', 'STORE']),
  can_distribute: z.boolean().default(false),
  min_total: z.number().int().min(0).default(0),
});
export const UpdateLocationBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  can_distribute: z.boolean(),
  min_total: z.number().int().min(0),
  active: z.boolean(),
});

// ----- Roles y usuarios -----
export const RoleSchema = z.object({
  id: z.number().int(),
  code: z.string(),
  name: z.string(),
  scoped: z.boolean(),
  permissions: z.array(z.string()),
});

export const UsernameSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9._-]{1,39}$/, 'INVALID_USERNAME');
/** Módulos habilitados para un usuario: el conjunto completo que debe quedarle (migración 0031). */
export const UserPermissionsBodySchema = z.object({
  permissions: z.array(z.string().trim().min(3).max(40)).max(100),
});
export const UserPermissionsResultSchema = z.object({
  user_id: z.number().int(),
  permissions: z.array(z.string()),
  exceptions: z.number().int(),
});
export type UserPermissionsResult = z.infer<typeof UserPermissionsResultSchema>;

export const OptionalEmailSchema = z.string().trim().toLowerCase().max(120).transform((v) => v || null).pipe(z.string().email().nullable()).nullable().optional();

export const UserSchema = z.object({
  id: z.number().int(),
  username: z.string(),
  email: z.string().nullable(),
  full_name: z.string(),
  active: z.boolean(),
  must_change_password: z.boolean(),
  preferred_language: LanguageSchema,
  role_code: z.string(),
  role_name: z.string(),
  role_scoped: z.boolean(),
  location_ids: z.array(z.number().int()),
  /** Permisos efectivos: los del rol con las excepciones del usuario ya aplicadas. */
  permissions: z.array(z.string()),
  /** Permisos en los que este usuario se aparta de su rol. */
  permission_exceptions: z.array(z.string()),
  created_at: z.string(),
});
export type User = z.infer<typeof UserSchema>;

export const CreateUserBodySchema = z.object({
  username: UsernameSchema,
  email: OptionalEmailSchema,
  full_name: z.string().trim().min(1).max(120),
  password: PasswordPolicy,
  role_code: z.string().regex(/^[a-z_]{2,30}$/),
  location_ids: z.array(IdSchema).default([]),
  preferred_language: LanguageSchema.default('es'),
});
export const UpdateUserBodySchema = z.object({
  email: OptionalEmailSchema,
  full_name: z.string().trim().min(1).max(120),
  role_code: z.string().regex(/^[a-z_]{2,30}$/),
  location_ids: z.array(IdSchema).default([]),
  active: z.boolean(),
});
export const ResetPasswordBodySchema = z.object({ new_password: PasswordPolicy });

// ----- Medios de pago -----
export const PaymentKindSchema = z.enum(['CASH', 'TRANSFER', 'CARD', 'DIRECT_CREDIT', 'FINANCIER']);
export const PaymentMethodSchema = z.object({
  id: z.number().int(),
  code: z.string(),
  name: z.string(),
  kind: PaymentKindSchema,
  collected: z.boolean(),
  active: z.boolean(),
  sort_order: z.number().int(),
});
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export const CreatePaymentMethodBodySchema = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_]{2,30}$/),
  name: z.string().trim().min(1).max(60),
  kind: PaymentKindSchema,
  active: z.boolean().default(true),
  sort_order: z.number().int().min(0).default(100),
});
export const UpdatePaymentMethodBodySchema = z.object({
  name: z.string().trim().min(1).max(60),
  active: z.boolean(),
  sort_order: z.number().int().min(0),
});

export const MovementTypeSchema = z.object({
  code: z.string(),
  name: z.string(),
  direction: z.number().int(),
  reversible: z.boolean(),
  active: z.boolean(),
});

export const FixedCostsBodySchema = z.object({
  rent: z.coerce.number().min(0).max(99_999_999_999).default(0),
  payroll: z.coerce.number().min(0).max(99_999_999_999).default(0),
  other_fixed: z.coerce.number().min(0).max(99_999_999_999).default(0),
});
export const ProfitabilitySchema = z.object({
  from: z.string(),
  to: z.string(),
  days: z.number().int(),
  today: z.string(),
  locations: z.array(z.object({
    location_id: z.number().int(), code: z.string(), name: z.string(), type: z.enum(['WAREHOUSE', 'STORE']), active: z.boolean(),
    units: z.number().int(), sales: z.number(), cost: z.number(), gross_margin: z.number(),
    external_sales: z.number().default(0), internal_sales: z.number().default(0), internal_margin: z.number().default(0),
    fixed_month: z.number(), fixed_prorated: z.number(), result: z.number(),
  })),
});
export type Profitability = z.infer<typeof ProfitabilitySchema>;
